import { Inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Router } from '@angular/router';
import { environment } from '../environments/environment';
import { NewRequestService } from './pages/new-request/new-request.service';
import { addRequestList } from './Store/request.actions';
import { Store } from '@ngrx/store';
import * as data from './countries.json';
import { AppConstant } from './app.constants';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { DecimalPipe } from '@angular/common';
import { CommonUtilityService } from './utility/common-utility.service';
import { FormArray, FormGroup } from '@angular/forms';

export interface AddressDetails {
  country?: string;
  subdivision?: string;
  county?: string;
  city?: string;
  neighborhood?: string;
  streetNumber?: string;
  streetName?: string;
  postalCode?: string;
  fullAddress?: string;
}

type ZoomForCountries = {
  [country: string]: number;
};

/** Safely converts any caught value to a human-readable message string. */
function toErrorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  try {
    const json = JSON.stringify(err);
    return json ?? Object.prototype.toString.call(err);
  } catch {
    return Object.prototype.toString.call(err) as string;
  }
}

/** Wraps any caught value in an Error, preserving the message. */
function toError(err: unknown): Error {
  if (err instanceof Error) return err;
  return new Error(toErrorMessage(err));
}

/** Extracts a safe message string from an HTMLElement onerror event argument. */
function scriptErrorMessage(event: Event | string): string {
  if (typeof event === 'string') return event;
  if (event instanceof ErrorEvent) return event.message;
  return 'Failed to load script';
}

// ---------------------------------------------------------------------------
// hasOnlySpacesOrSpecialChars helpers — extracted to reduce cognitive complexity
// ---------------------------------------------------------------------------

/**
 * Normalises a field label for display in snackbar messages.
 * Replaces internal field-name fragments with user-friendly equivalents.
 */
function normaliseFieldLabel(label: string): string {
  return label
    .replaceAll(/Street\s*No/gi, 'Address line') // replaceAll preferred over replace (prefer-string-replaceall)
    .replaceAll(/Org/gi, 'Organization');
}

/** Returns true when the key matches a "name-like" field that must contain letters. */
function isNameLikeKey(key: string): boolean {
  return /(org|name|firstname|familyname|city|company\s*name)/i.test(key);
}

/**
 * Validates a single string control value and shows a snackbar on failure.
 * Returns false if any validation rule fails, true otherwise.
 */
function validateStringValue(
  value: string,
  key: string,
  fullKey: string,
  formatFieldLabel: ((field: string) => string) | undefined,
  showSnackbar: (msg: string) => void
): boolean {
  const trimmed = value.trim();
  const label = normaliseFieldLabel(formatFieldLabel ? formatFieldLabel(fullKey) : fullKey);

  if (trimmed === '') {
    showSnackbar(`${label} must not contain only spaces.`);
    return false;
  }

  if (/^[^a-zA-Z\d]+$/.test(trimmed)) {
    showSnackbar(`${label} must not contain only special characters.`);
    return false;
  }

  if (isNameLikeKey(key) && /^\d+$/.test(trimmed)) {
    showSnackbar(`${label} must not contain only numbers.`);
    return false;
  }

  if (isNameLikeKey(key) && !/[a-zA-Z]/.test(trimmed)) {
    showSnackbar(`${label} must contain at least one alphabet character.`);
    return false;
  }

  return true;
}

@Injectable({
  providedIn: 'root'
})
export class SharedService {
  renderer: Renderer2 | undefined;
  public errorMessage: string = '';
  public zoomSize: any;
  public countries: any = (data as any).default;
  public totalTax = 0;
  commaSeparatedAmount: any
  states: any[] = [];
  zoomForCountries: any = {
    'United States': 3,
    'Saint Lucia': 9,
    'Canada': 3,
    'Russia': 3,
    'India': 4,
    'Brazil': 3,
    'United Kingdom': 6,
    'Jamaica': 8,
    'The Bahamas': 7,
    'Cayman Islands': 9,
    'Algeria': 4,
    'Barbados': 9,
    'U.S. Virgin Islands': 11,
    'Saint Kitts & Nevis': 9
  }

  countryLogoAvailable = ['jamaica', 'barbados'];
  scriptLoaded$ = new BehaviorSubject<boolean>(false);
  scriptUrl = 'https://app.termly.io/resource-blocker/6e829330-ee66-4cdc-9144-02299d825568?autoBlock=on';
  private progressBarSubject = new BehaviorSubject<boolean>(false);
  progressBar$ = this.progressBarSubject.asObservable();

  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  isLoading$ = this.isLoadingSubject.asObservable();

  constructor(
    private router: Router,
    private requestService: NewRequestService,
    private store: Store,
    @Inject(DOCUMENT) private _document: Document,
    private http: HttpClient,
    public decimalPipe: DecimalPipe,
    rendererFactory: RendererFactory2,
    private commonService: CommonUtilityService
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
    try {
      const win: any = window as any;
      if (!win.gm_authFailure) {
        win.gm_authFailure = () => {
          console.error('[SharedService] Google Maps authentication failure (gm_authFailure)');
          try { this.scriptLoaded$.next(false); } catch { /* no-op */ }
          try {
            this.commonService.showSnackbar(
              'Map failed to authorize. Check API key, referer restrictions and billing in Google Cloud Console.'
            );
          } catch { /* no-op */ }
        };
      }
    } catch { /* no-op: window may be unavailable in SSR */ }
  }

  loadGoogleMapsScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const win: any = window as any;
        if (win.google && win.google.maps) {
          resolve();
          return;
        }

        const existing: HTMLScriptElement | null = this._document.querySelector('script[data-gmaps-loader]');
        if (existing) {
          if (win.google?.maps) {
            resolve();
          } else {
            existing.addEventListener('load', () => resolve());
            existing.addEventListener('error', (e) => reject(toError(e)));
          }
          return;
        }

        const apiKey = environment?.gmap_key ?? '';
        if (!apiKey) {
          console.warn('Google Maps API key not found in environment.gmap_key');
          resolve();
          return;
        }

        const script: HTMLScriptElement = this._document.createElement('script');
        script.setAttribute('data-gmaps-loader', 'true');
        const src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,geometry&loading=async&v=weekly`;
        script.src = src;
        script.async = true;
        script.defer = true;
        console.info('[SharedService] Injecting Google Maps script', src, 'apiKeyLength=', apiKey.length);

        script.onload = () => {
          console.info('[SharedService] Google Maps script loaded successfully');
          try { this.scriptLoaded$.next(true); } catch { /* no-op */ }
          resolve();
        };

        script.onerror = (event: Event | string) => {
          const message = scriptErrorMessage(event);
          const error = new Error(message);
          console.error('[SharedService] Failed to load Google Maps script:', message, src);
          try { this.scriptLoaded$.next(false); } catch { /* no-op */ }
          try {
            this.commonService.showSnackbar(
              'Map failed to load. See console for details (API key, network, or Google Cloud settings).'
            );
          } catch { /* no-op */ }
          reject(error);
        };

        this._document.head.appendChild(script);
      } catch (err) {
        reject(toError(err));
      }
    });
  }

  setProgressBarState(state: boolean) {
    this.progressBarSubject.next(state);
  }

  setIsLoadingState(state: boolean) {
    this.isLoadingSubject.next(state);
  }

  getZoomForCountry(country: any) {
    return this.zoomForCountries?.[country] ?? 6;
  }

  public getCountryFromcode = function (country: string) {
    let name = "";
    if (country) {
      const i = this.countries.findIndex(x => x.alpha2.toLowerCase() == country.toLowerCase());
      name = i >= 0 ? this.countries[i].name : country;
    }
    return name;
  }

  performSearch() {
    this.setIsLoadingState(true);
    this.store.dispatch(addRequestList({ requests: [] }));
    const id = localStorage.getItem('brokerId');
    const search_id = localStorage.getItem('search_id');
    const params = JSON.parse(localStorage.getItem('search_params'));
    const page: any = localStorage.getItem('search_page');
    const pageSize: any = localStorage.getItem('search_pageSize');
    if (search_id) {
      params['id'] = search_id;
    }
    this.requestService.searchBrokerRequest(id, params, Number(page) + 1, pageSize).subscribe({
      next: (response: any[]) => {
        this.store.dispatch(addRequestList({ requests: response }));
        this.setIsLoadingState(false);
      },
      error: (error: any) => {
        this.store.dispatch(addRequestList({ requests: [] }));
        this.setIsLoadingState(false);
      }
    });
  }

  checkMatching(request: any, structure: any) {
    return (request == null && structure == null) || request.toString() === structure.toString();
  }

  makeNBI(data: any, status: any) {
    if (!data?.insurable_interest?.length) return { data: [] };

    const isPolygonAgent = data?.PricingAdapter?.agentName?.toLowerCase().includes('polygon');

    const loc_obj = data.insurable_interest.reduce(
      (acc: any, curr: any) => {

        if (curr?.Calculations != null) return acc;

        if (isPolygonAgent) {

          const latArr = (curr?.insurableInterestLocationLat || '')
            .split(',')
            .map((s: string) => s.trim())
            .filter(Boolean);

          const lngArr = (curr?.insurableInterestLocationLng || '')
            .split(',')
            .map((s: string) => s.trim())
            .filter(Boolean);

          if (!latArr.length || !lngArr.length) return acc;
          if (latArr.length !== lngArr.length) return acc;

          const pairs = latArr.map((lat: string, i: number) => [lat, lngArr[i]]);

          if (pairs.length < 4) return acc;

          const uniquePairs = new Set(pairs.map(p => `${p[0]}_${p[1]}`));
          if (uniquePairs.size < 3) return acc;

          const [firstLat, firstLng] = pairs[0];
          const [lastLat, lastLng] = pairs[pairs.length - 1];

          if (firstLat !== lastLat || firstLng !== lastLng) return acc;
        }

        acc.data.push({
          ...curr,
          locations: [
            {
              insurableInterestLocationLat: curr.insurableInterestLocationLat,
              insurableInterestLocationLng: curr.insurableInterestLocationLng,
              id: curr.id
            }
          ]
        });

        return acc;
      },
      { data: [] }
    );

    return loc_obj;
  }

  updateAmount(value: string | number, i?: any): string {
    if (!value) return '';
    const raw = typeof value === 'number' ? value.toString() : value;
    return this.addCommas(raw.replaceAll(/\D/g, ''));
  }

  addCommas(value: any): string {
    if (value === null || value === undefined) return '';
    return String(value).replaceAll(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  getIntegerPayout(value) {
    return value ? Math.round(value * 100) : value;
  }

  getFloatPayout(value) {
    return value ? (value == 100 ? 1 : (value / 100)) : value;
  }

  getOriginalPayout(value) {
    return value ? (value === 1 ? 100 : value * 100) : value;
  }

  loadDistricts(): Observable<any[]> {
    return new Observable(observer => {
      this.http.get('assets/districts.csv', { responseType: 'text' })
        .subscribe({
          next: (data: string) => {
            const csvToRowArray = data.split("\n");
            if (csvToRowArray.length < 2) {
              observer.error(new Error('CSV data is empty or malformed'));
              return;
            }
            const districts: any[] = [];
            for (let index = 1; index < csvToRowArray.length; index++) {
              const row = csvToRowArray[index].split(",");
              if (row.length < 9) continue;
              districts.push({
                id: row[0]?.trim(),
                name: row[1]?.trim(),
                country_id: row[2]?.trim(),
                country_code: row[3]?.trim(),
                country_name: row[4]?.trim(),
                state_code: row[5]?.trim(),
                type: row[6]?.trim(),
                latitude: row[7]?.trim(),
                longitude: row[8]?.trim()
              });
            }
            observer.next(districts);
            observer.complete();
          },
          error: (err: unknown) => {
            observer.error(toError(err));
          }
        });
    });
  }

  loadStates(): Observable<any[]> {
    return new Observable(observer => {
      this.http.get('assets/states.csv', { responseType: 'text' })
        .subscribe({
          next: (data: string) => {
            const csvToRowArray = data.split("\n");
            if (csvToRowArray.length < 2) {
              observer.error(new Error('CSV data is empty or malformed'));
              return;
            }
            const states: any[] = [];
            for (let index = 1; index < csvToRowArray.length; index++) {
              const row = csvToRowArray[index].split(",");
              if (row.length < 9) continue;
              states.push({
                id: row[0]?.trim(),
                name: row[1]?.trim(),
                country_id: row[2]?.trim(),
                country_code: row[3]?.trim(),
                country_name: row[4]?.trim(),
                state_code: row[5]?.trim(),
                type: row[6]?.trim(),
                latitude: row[7]?.trim(),
                longitude: row[8]?.trim()
              });
            }
            observer.next(states);
            observer.complete();
          },
          error: (err: unknown) => {
            observer.error(toError(err));
          }
        });
    });
  }

  restrictKeys(event: KeyboardEvent): void {
    const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'];
    if (allowedKeys.includes(event.key)) return;
    if (/^\d$/.test(event.key)) return;
    event.preventDefault();
  }

  numberBetweenZeroToHundred(event: any): void {
    const value = Number(event.target.value);
    if (value < 0) {
      event.target.value = 0;
    } else if (value > 100) {
      event.target.value = 100;
    }
  }

  numberOnly(event: KeyboardEvent, type: string = ''): boolean {
    const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];
    if (allowedKeys.includes(event.key)) return true;

    // date: digits, forward-slash, hyphen; number/default: digits only
    const pattern = type.toLowerCase() === 'date' ? /^[\d/-]$/ : /^\d$/;

    if (!pattern.test(event.key)) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  numberWithMinus(event: KeyboardEvent, value: string = ''): boolean {
    const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'];
    if (allowedKeys.includes(event.key)) return true;

    if (/\p{Extended_Pictographic}/u.test(event.key)) {
      event.preventDefault();
      return false;
    }

    if (/^\d$/.test(event.key)) return true;

    if (event.key === '-') {
      const input = event.target as HTMLInputElement;
      const cursorPos = input.selectionStart ?? 0;
      return !value.includes('-') && cursorPos === 0;
    }

    if (event.key === '.' && !value.includes('.') && value !== '-' && value.length > 0) {
      return true;
    }

    return false;
  }

  alphanumericOnly(event: KeyboardEvent, name = ''): boolean {
    const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'];
    if (allowedKeys.includes(event.key)) return true;

    const inputElement = event.target as HTMLInputElement;

    if (/\p{Extended_Pictographic}/u.test(event.key)) {
      event.preventDefault();
      return false;
    }

    if (event.key === ' ') {
      const currentValue = inputElement.value;
      if (currentValue.length === 0 || currentValue.endsWith(' ')) {
        event.preventDefault();
        return false;
      }
      return true;
    }

    // Unnecessary escape on \- removed (no-useless-escape): hyphen is safe at start of class
    const isAllowedChar = /^[a-zA-Z\d.,@_!#$%&*()+`~='"-]$/.test(event.key);
    if (!isAllowedChar) {
      event.preventDefault();
      return false;
    }

    return true;
  }

  removeEmojis(value: string, name = ''): string {
    if (!value || typeof value !== 'string') return '';

    const withoutEmojis = value.replaceAll(/\p{Extended_Pictographic}+/gu, '');

    if (name === 'lat' || name === 'lng') {
      const cleaned = withoutEmojis.replaceAll(/[^\d.-]/g, '');
      // RegExp.exec() preferred over String.match() when not using /g flag (prefer-regexp-exec)
      const match = /^-$|^-?\d+(\.\d*)?$/.exec(cleaned);
      return match?.[0] ?? '';
    }

    return withoutEmojis.replaceAll(/ {2,}/g, ' ');
  }

  blockSpaceAndSpecialKeys(event: KeyboardEvent) {
    if (event.key === ' ') {
      event.stopPropagation();
    }
  }

  isValueZeroLimit(iiVal: any[], page = ''): boolean {
    const isMainPage = page === '';

    for (let i = 0; i < iiVal.length; i++) {
      const item = iiVal[i];
      const indexText = ` at Insurable Interest ${i + 1}.`;

      const checks = isMainPage
        ? [
            { value: item.insuredRiskFactorValue, label: 'Exposure Value' },
            { value: item.propertyInsuredValue, label: 'Property Insured Value' }
          ]
        : [
            { value: item.reinsuranceExposureValue, label: 'Exposure Value' }
          ];

      for (const { value, label } of checks) {
        if (value !== undefined && value !== null && value !== '' && Number(value) === 0) {
          const suffix = isMainPage ? indexText : ' at Insurer.';
          this.commonService.showSnackbar(`${label} cannot be 0${suffix}`);
          return false;
        }
      }
    }
    return true;
  }

  isPremiumZero(value: any): boolean {
    if (+value === 0 && value) {
      this.commonService.showSnackbar(`Premium Budget cannot be 0 at Contract Type.`);
      return false;
    }
    return true;
  }

  /**
   * Validates all string controls in a reactive form group/array.
   * Cognitive complexity kept low by delegating per-field logic to
   * the module-level `validateStringValue` helper.
   */
  private validateFormGroup(
    formGroup: FormGroup,
    formatFieldLabel: ((field: string) => string) | undefined,
    parentKey: string,
    invalidKeys?: string[] 
  ): boolean {
    let allFieldsValid = true;
    for (const key of Object.keys(formGroup.controls)) {
      const control = formGroup.get(key);
      const fullKey = parentKey ? `${parentKey}.${key}` : key;
      if (control instanceof FormGroup || control instanceof FormArray) {
        if (!this.hasOnlySpacesOrSpecialChars(control, formatFieldLabel, fullKey)) {
          allFieldsValid = false;
        }
        continue;
      }
      const value = control?.value;
      if (value == null || value === '' || typeof value !== 'string') continue;
      const isValid = validateStringValue(
        value, key, fullKey, formatFieldLabel,
        this.commonService.showSnackbar.bind(this.commonService)
      );
      if (!isValid) {
        allFieldsValid = false;
        invalidKeys?.push(key);              // ← collect invalid key
        control?.setErrors({ invalidChars: true });  // ← mark control invalid
        control?.markAsTouched();
      }
    }
    return allFieldsValid;
  }

  private validateFormArray(
    formArray: FormArray,
    formatFieldLabel: ((field: string) => string) | undefined,
    parentKey: string
  ): boolean {
    let allFieldsValid = true;
    for (let index = 0; index < formArray.controls.length; index++) {
      const control = formArray.at(index);
      const fullKey = `${parentKey}[${index}]`;
      if (control instanceof FormGroup || control instanceof FormArray) {
        if (!this.hasOnlySpacesOrSpecialChars(control, formatFieldLabel, fullKey)) {
          allFieldsValid = false;
        }
      }
    }
    return allFieldsValid;
  }
  
  hasOnlySpacesOrSpecialChars(
  formGroup: FormGroup | FormArray,
  formatFieldLabel?: (field: string) => string,
  parentKey: string = '',
  invalidKeys: string[] = []   
): boolean {
  if (formGroup instanceof FormGroup) {
    return this.validateFormGroup(formGroup, formatFieldLabel, parentKey, invalidKeys);
  }
  return this.validateFormArray(formGroup as FormArray, formatFieldLabel, parentKey);
}

  getLocationFromPlaceId(placeId: string): Promise<{ lat: number; lng: number } | null> {
    const apiKey = environment.gmap_key;
    const url = `https://maps.googleapis.com/maps/api/place/details/json?place_id=${placeId}&key=${apiKey}`;

    return fetch(url)
      .then(res => res.json())
      .then(data => {
        const location = data.result?.geometry?.location;
        if (!location) return null;
        const { lat, lng } = location;
        return { lat, lng };
      })
      .catch((err: unknown) => {
        console.error('Place ID fetch error:', toErrorMessage(err));
        return null;
      });
  }

  geocodePlaceName(query: string): Promise<{ lat: number; lng: number } | null> {
    const apiKey = environment.gmap_key;
    const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(query)}&key=${apiKey}`;

    return fetch(url)
      .then(res => res.json())
      .then(data => {
        // optional chain preferred (prefer-optional-chain)
        const location = data.results?.[0]?.geometry?.location;
        if (!location) return null;
        const { lat, lng } = location;
        return { lat, lng };
      })
      .catch((err: unknown) => {
        console.error('Geocode place name error:', toErrorMessage(err));
        return null;
      });
  }

  reverseGeocode(lat: number, lng: number): Promise<{
    country: string;
    state?: string;
    county?: string;
  } | null> {
    const apiKey = environment.gmap_key;
    const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`;

    return fetch(url)
      .then(res => res.json())
      .then(data => {
        const result = data.results?.[0];
        if (!result) return null;

        let country = '';
        let state = '';
        let county = '';

        for (const component of result.address_components) {
          const types: string[] = component.types;
          if (types.includes('country')) country = component.short_name;
          if (types.includes('administrative_area_level_1')) state = component.long_name;
          if (types.includes('administrative_area_level_2')) county = component.long_name;
        }

        return { country, state, county };
      })
      .catch((err: unknown) => {
        console.error('Reverse geocoding error:', toErrorMessage(err));
        return null;
      });
  }

  onMapUrlInput(inputRef: HTMLInputElement) {
    const inputValue = inputRef.value || '';
    if (inputValue === '') return;

    const parts = inputValue.split('https://www.google.com/maps').filter(Boolean);

    if (parts.length > 0) {
      inputRef.value = 'https://www.google.com/maps' + parts.at(-1).trim();
    } else {
      inputRef.value = '';
      this.commonService.showSnackbar('Please enter a valid Google Maps URL.');
    }
  }

  removeValueFromLocalStorage() {
    const keys = [
      'adapterParams', 'calculatedValues', 'categories', 'type',
      'thresholdStart', 'thresholdEnd', 'premiumGross', 'dataSource',
      'premiumCurrencySymbol', 'myFormData', 'name', 'lastValue', 'isSimpsonSelected'
    ];
    keys.forEach(key => localStorage.removeItem(key));
  }

  enterValueMin10Max100(event: any) {
    const input = event.target;
    const value = Number.parseInt(input.value, 10);
    const min = 10;
    const max = 100;
  
    if (isNaN(value)) {
      input.value = min;
      input.dispatchEvent(new Event('input'));
      return;
    }
  
    if (value < min || value > max) {
      input.value = value < min ? min : max;
      input.dispatchEvent(new Event('input'));
    }
  }

  blockDotAndMinus(event: KeyboardEvent) {
    if (event.key === '-' || event.key === '.') {
      event.preventDefault();
    }
  }

  removeEnclosingQuotes(str: string): string {
    if (
      (str?.startsWith('"') && str?.endsWith('"')) ||
      (str?.startsWith("'") && str?.endsWith("'"))
    ) {
      return str.slice(1, -1);
    }
    return str;
  }

  private loadRecaptchaScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (document.getElementById('recaptcha-script')) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.id = 'recaptcha-script';
      script.src = `https://www.google.com/recaptcha/api.js?render=${environment.captcha_siteKey}`;

      script.onload = () => resolve();
      script.onerror = (event: Event | string) => {
        reject(new Error(scriptErrorMessage(event)));
      };

      document.body.appendChild(script);
    });
  }

  async executeRecaptcha(action: string): Promise<string> {
    await this.loadRecaptchaScript();

    return new Promise((resolve, reject) => {
      if (!window['grecaptcha']) {
        reject(new Error('reCAPTCHA not available'));
        return;
      }

      window['grecaptcha'].ready(() => {
        window['grecaptcha']
          .execute(environment.captcha_siteKey, { action })
          .then((token: string) => resolve(token))
          .catch((err: unknown) => reject(toError(err)));
      });
    });
  }
}